import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { ModernSidebar } from "./Sidebar";
import { useActivityTracker } from "@/hooks/useActivityTracker";
import {
  SidebarProvider,
  SidebarInset,
  useSidebar,
  SidebarTrigger,
} from "@/components/ui/sidebar";
import { SideRailProvider, useSideRail } from "@/contexts/SideRailContext";
import { AskHobbesChat } from "@/components/dashboard/AskHobbesChat";
import { AnimatePresence, motion } from "framer-motion";
import { MOBILE_BREAKPOINT, useIsMobile } from "@/hooks/use-mobile";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { TopNavigation } from "@/components/layout/TopNavigation";
import { useOrganization } from "@/hooks/useOrganization";
import askHobbesHistoryService from "@/lib/api/askHobbesHistory";
import { queryKeys } from "@/queries/keys";

interface LayoutProps {
  children: React.ReactNode;
}

const pageVariants = {
  initial: { opacity: 0, y: 6 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -2 },
};

const pageTransition = {
  duration: 0.15,
  ease: [0.25, 0.1, 0.25, 1] as const,
};

function LayoutContent({ children }: LayoutProps) {
  const { isOpen, openSideRail, closeSideRail } = useSideRail();
  const { setOpen } = useSidebar();
  const { currentOrganization } = useOrganization();
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();
  const location = useLocation();

  // Auto-collapse sidebar when Ask Hobbes side rail opens (only on smaller screens)
  useEffect(() => {
    if (isOpen && typeof window !== "undefined" && window.innerWidth < MOBILE_BREAKPOINT) {
      setOpen(false);
    }
  }, [isOpen, setOpen]);

  useEffect(() => {
    if (!currentOrganization?.id) return;

    queryClient.prefetchQuery({
      queryKey: queryKeys.askHobbesHistory.list(currentOrganization.id),
      queryFn: async () => {
        const res = await askHobbesHistoryService.getChats(currentOrganization.id);
        if (!res.success || !res.data) {
          throw new Error(res.error ?? 'Failed to fetch Ask Hobbes chats');
        }
        return res.data;
      },
      staleTime: 5 * 60_000,
    });
  }, [currentOrganization?.id, queryClient]);

  const handleSideRailChange = (open: boolean) => {
    if (open) {
      openSideRail();
    } else {
      closeSideRail();
    }
  };

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden p-2">
      <div className="sticky top-0 z-30 -mx-2 flex items-center justify-between border-b bg-background/95 px-2 py-2 backdrop-blur sm:-mx-3 sm:px-3 md:hidden">
        <div className="flex items-center gap-2 min-w-0">
          <SidebarTrigger className="h-9 w-9" />
        </div>
        <TopNavigation className="gap-2" />
      </div>

      <div className="flex min-h-0 flex-1 gap-4 overflow-hidden">
        <div className="flex min-h-0 min-w-0 flex-1 overflow-hidden rounded-lg border border-border/80 bg-background">
          <div className="min-h-0 min-w-0 flex-1 overflow-y-auto overflow-x-hidden">
            <AnimatePresence mode="wait">
              <motion.div
                key={location.pathname}
                variants={pageVariants}
                initial="initial"
                animate="animate"
                exit="exit"
                transition={pageTransition}
                className="h-full"
              >
                {children}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
        {!isMobile && (
          <AnimatePresence mode="wait">
            {isOpen && <AskHobbesChat />}
          </AnimatePresence>
        )}
      </div>

      {isMobile && (
        <Sheet open={isOpen} onOpenChange={handleSideRailChange}>
          <SheetContent
            side="right"
            className="h-full w-full p-0 [&>button]:hidden"
          >
            <AskHobbesChat />
          </SheetContent>
        </Sheet>
      )}
    </div>
  );
}

export function Layout({ children }: LayoutProps) {
  // Enable activity tracking for session extension
  useActivityTracker();

  return (
    <SideRailProvider>
      <SidebarProvider defaultOpen={false}>
        <ModernSidebar />
        <SidebarInset>
          <LayoutContent>{children}</LayoutContent>
        </SidebarInset>
      </SidebarProvider>
    </SideRailProvider>
  );
}
